import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { Form } from 'antd';

import { DASHBOARD_ITEM_API_NAMES, SCHEDULE_TYPES } from '@src/constants/commons';
import useSelectGroupsWithPagination from '@src/packages/utils/hooks/collections/useSelectGroupsWithPagination';
import useGetEventTypes from '@src/packages/utils/hooks/useGetEventTypes';
import ChartService from '@src/services/ChartService';

import { FILTER_DATA_OPTIONS } from '../../../components/Shared/constants';
import {
  ALL_TEMPLATES,
  DASHBOARDS_EVENT_TYPE,
  defaultAggsConfig,
  LAYOUT_BY_COMPONENT_TYPE,
  USERS_TYPE,
} from '../../../constants';
import { useEventsByScheduleContext } from '../../../contexts/EventsByScheduleContext';
import { aggOptions, aggregationFactory } from '../constants';

const PAGE_SIZE = 20;

const getLocationCombinerValue = queryParams => {
  const { locationIds, locationGroupId } = queryParams || {};
  if (locationGroupId) {
    return { isAGroup: true, data: { id: locationGroupId } };
  }
  if (locationIds?.length) {
    return { isAGroup: false, data: locationIds.map(id => ({ id })) };
  }
  return null;
};

const useEventsForm = ({ chartId, divId, boardId }) => {
  const [form] = Form.useForm();
  const chartService = useMemo(() => new ChartService(), []);
  const orgId = useSelector(({ organizations }) => organizations?.organization?.id);

  const { eventsFromSchedules: eventTypesFromSchedule, fetchEventsFromSchedules } =
    useEventsByScheduleContext();

  const [type, setType] = useState(null);
  const [selectedSchedules, setSelectedSchedules] = useState({ byId: {}, selectedAll: false });
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [initialScheduleIds, setInitialScheduleIds] = useState([]);
  const [fetchedChartData, setFetchedChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAggs, setSelectedAggs] = useState(defaultAggsConfig);
  const [color, setColor] = useState(
    Object.fromEntries(aggOptions.map(opt => [opt, defaultAggsConfig[opt]?.color])),
  );
  const [chartTypes, setChartTypes] = useState(
    Object.fromEntries(aggOptions.map(opt => [opt, defaultAggsConfig[opt]?.displayType])),
  );

  const watchUsersType = Form.useWatch('usersType', form);
  const watchDivisionIds = Form.useWatch('divisionId', form);
  const watchFormId = Form.useWatch('formId', form);

  const {
    data: groupOptions,
    loading: groupsLoading,
    handlePopupScroll: handlePopupScrollGroups,
  } = useSelectGroupsWithPagination({
    orgId,
    pageSize: PAGE_SIZE,
    divIds: divId ? [divId] : null,
    orderByConfig: { field: 'name', direction: 'asc' },
  });

  const { data: eventTypes, loading: eventTypesLoading } = useGetEventTypes(
    true,
    divId || watchDivisionIds,
  );

  const eventTypesWithNumComponents = useMemo(() => {
    const output = eventTypes;
    const event = output?.find(e => e?.id === watchFormId);
    if (!event) return [];
    return (
      event?.components
        ?.filter(({ type: t }) => t === 'number')
        .map(component => ({ label: component.label?.name, value: component.id })) || []
    );
  }, [eventTypes, watchFormId]);

  const mainLoading = eventTypesLoading || loading;
  const filterDataOptions = useMemo(
    () => [...FILTER_DATA_OPTIONS].filter(o => o.value !== SCHEDULE_TYPES.checkpoint_route),
    [],
  );

  const buildBody = useCallback(() => {
    const {
      title,
      description,
      usersType,
      userIds,
      groupIds,
      formId,
      triggerId,
      componentId,
      schedulesIds,
      divisionId,
      templateIds,
      type: typeVal,
      layout,
      locationIds: location,
    } = form.getFieldsValue();

    const usersOrGroups = {};
    if (usersType === USERS_TYPE.ALL) {
      usersOrGroups.userIds = [];
      usersOrGroups.groupIds = [];
    } else if (usersType === USERS_TYPE.USERS) {
      usersOrGroups.userIds = userIds;
      usersOrGroups.groupIds = [];
    } else if (usersType === USERS_TYPE.GROUPS) {
      usersOrGroups.userIds = [];
      usersOrGroups.groupIds = groupIds;
    }

    const locationIdsOrGroup = {};
    if (location?.isAGroup === true && location?.data?.id) {
      locationIdsOrGroup.locationGroupId = location.data.id;
    } else if (location?.isAGroup === false && location?.data?.length) {
      locationIdsOrGroup.locationIds = location.data.map(loc => loc.id);
    }

    const aggregations = aggregationFactory(selectedAggs, color, chartTypes);

    const baseBody = {
      title: title ?? '',
      description: description ?? '',
      apiName: DASHBOARD_ITEM_API_NAMES.NUMBER_METRICS_DASHBOARD,
      layout: layout || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.CHARTS],
      aggregations,
      queryParams: {
        ...usersOrGroups,
        formId: formId ?? null,
        triggerId: triggerId ?? null,
        componentId: componentId ?? null,
        templateIds:
          typeVal === null ? [] : templateIds?.includes(ALL_TEMPLATES) ? [] : templateIds,
        scheduleIds: typeVal === null ? [] : selectedSchedules.selectedAll ? [] : schedulesIds,
        divisionIds: divisionId ? [divisionId] : [],
        type: typeVal ?? null,
        ...locationIdsOrGroup,
      },
    };

    const body = fetchedChartData ? { ...fetchedChartData, ...baseBody } : baseBody;
    form.setFieldValue('formData', body);
    setType(typeVal ?? null);
  }, [form, selectedSchedules.selectedAll, selectedAggs, color, chartTypes, fetchedChartData]);

  const handleClear = useCallback(() => {
    setType(null);
    setSelectedSchedules({ byId: {}, selectedAll: false });
    setSelectedAggs(defaultAggsConfig);
    setColor(Object.fromEntries(aggOptions.map(opt => [opt, defaultAggsConfig[opt]?.color])));
    setChartTypes(
      Object.fromEntries(aggOptions.map(opt => [opt, defaultAggsConfig[opt]?.displayType])),
    );
    form.resetFields();
  }, [form]);

  const handleTypeChange = useCallback(() => {
    form.setFieldsValue({ schedulesIds: [], templateIds: [] });
    setSelectedSchedules({ byId: {}, selectedAll: true });
    buildBody();
  }, [form, buildBody]);

  const handleDivisionChange = useCallback(() => {
    form.setFieldsValue({
      componentId: null,
      formId: null,
      triggerId: null,
      locationIds: null,
    });
    buildBody();
  }, [form, buildBody]);

  const handleTriggerChange = useCallback(
    trigger => {
      form.setFieldValue('triggerId', trigger?.id ?? null);
      buildBody();
    },
    [form, buildBody],
  );

  const handleFormChange = useCallback(() => {
    form.setFieldValue('triggerId', null);
    form.setFieldValue('componentId', null);
    buildBody();
  }, [form, buildBody]);

  const resetUsersAndGroups = useCallback(() => {
    form.setFieldValue('userIds', []);
    form.setFieldValue('groupIds', []);
  }, [form]);

  const toggleAggregation = useCallback((isOn, opt) => {
    if (isOn) {
      setSelectedAggs(curr => ({ ...curr, [opt]: defaultAggsConfig[opt] }));
      setChartTypes(prev => ({ ...prev, [opt]: defaultAggsConfig[opt]?.displayType }));
      setColor(prev => ({ ...prev, [opt]: defaultAggsConfig[opt]?.color }));
    } else {
      setSelectedAggs(({ [opt]: _, ...rest }) => rest);
      setChartTypes(prev => ({ ...prev, [opt]: null }));
      setColor(prev => ({ ...prev, [opt]: null }));
    }
  }, []);

  const handleAggColor = useCallback((opt, rgbValue) => {
    setColor(prev => ({ ...prev, [opt]: rgbValue }));
  }, []);

  const handleAggChartType = useCallback((opt, chartType) => {
    setChartTypes(prev => ({ ...prev, [opt]: chartType }));
  }, []);

  useEffect(() => {
    if (watchFormId && orgId) {
      fetchEventsFromSchedules(orgId, divId, watchFormId);
    }
  }, [watchFormId, orgId, divId, fetchEventsFromSchedules]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chartId || !boardId || !orgId) return;

      setLoading(true);
      try {
        const res = await chartService.getChart({
          orgId,
          divId,
          boardId,
          chartId,
        });
        if (res) {
          setFetchedChartData(res);
          const {
            title: titleFetched,
            description: descriptionFetched,
            queryParams: queryParamsFetched = {},
            layout: layoutFetched,
            aggregations: aggregationsFetched = {},
          } = res;

          const {
            userIds: userIdsFetched = [],
            groupIds: groupIdsFetched = [],
            formId: formIdFetched = null,
            componentId: componentIdFetched = null,
            scheduleIds: scheduleIdsFetched = [],
            templateIds: templateIdsFetched = [],
            divisionIds: divisionIdsFetched = [],
            triggerId: triggerIdFetched = null,
            locationIds: locationIdsFetched,
            locationGroupId: locationGroupIdFetched,
            type: typeFetched = null,
            unit = null,
          } = queryParamsFetched;

          let usersTypeFetched = USERS_TYPE.ALL;
          if (userIdsFetched?.length > 0) {
            usersTypeFetched = USERS_TYPE.USERS;
          } else if (groupIdsFetched?.length > 0) {
            usersTypeFetched = USERS_TYPE.GROUPS;
          }

          const aggColors = {};
          const aggTypes = {};
          aggOptions.forEach(opt => {
            if (aggregationsFetched[opt]) {
              aggColors[opt] = aggregationsFetched[opt].color || defaultAggsConfig[opt]?.color;
              aggTypes[opt] =
                aggregationsFetched[opt].displayType || defaultAggsConfig[opt]?.displayType;
            }
          });

          setSelectedAggs(aggregationsFetched || defaultAggsConfig);
          setColor(aggColors);
          setChartTypes(aggTypes);
          setType(typeFetched || null);

          form.setFieldsValue({
            id: chartId,
            title: titleFetched || '',
            description: descriptionFetched || '',
            userIds: userIdsFetched || [],
            groupIds: groupIdsFetched || [],
            formId: formIdFetched || null,
            componentId: componentIdFetched || null,
            scheduleIds: scheduleIdsFetched || [],
            templateIds: templateIdsFetched || [],
            divisionId: divisionIdsFetched[0] || null,
            triggerId: triggerIdFetched || null,
            locationIds: getLocationCombinerValue({
              locationIds: locationIdsFetched,
              locationGroupId: locationGroupIdFetched,
            }),
            type: typeFetched || null,
            layout: layoutFetched || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.CHARTS],
            usersType: usersTypeFetched,
            unit: unit || null,
          });
        }
      } catch (error) {
        console.error('Error loading chart:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [chartId, boardId, divId, chartService, form, orgId]);

  useEffect(() => {
    buildBody();
  }, [buildBody, selectedAggs, color, chartTypes]);

  useEffect(() => {
    if (
      fetchedChartData?.queryParams?.componentId &&
      eventTypes?.length &&
      !fetchedChartData?.queryParams?.formId
    ) {
      const componentId = fetchedChartData.queryParams.componentId;
      const formId = eventTypes.find(et => et.components?.some(c => c.id === componentId))?.id;
      if (formId) {
        form.setFieldValue('formId', formId);
      }
    }
  }, [fetchedChartData, eventTypes, form]);

  return {
    form,
    buildBody,
    handleClear,
    type,
    selectedSchedules,
    setSelectedSchedules,
    loadingSchedules,
    setLoadingSchedules,
    initialScheduleIds,
    setInitialScheduleIds,
    watchDivisionIds,
    eventTypes,
    eventTypesLoading,
    eventTypesWithNumComponents,
    orgId,
    fetchedChartData,
    handleTypeChange,
    handleDivisionChange,
    groupOptions,
    groupsLoading,
    handlePopupScrollGroups,
    watchUsersType,
    resetUsersAndGroups,
    filterDataOptions,
    handleTriggerChange,
    handleFormChange,
    loading: mainLoading,
    selectedAggs,
    color,
    chartTypes,
    toggleAggregation,
    handleAggColor,
    handleAggChartType,
    eventTypesFromSchedule,
  };
};

export default useEventsForm;
